import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Card, Typography, Chip, Avatar, Button, CircularProgress,
    Table, TableBody, TableCell, TableRow, TableHead, TableContainer,
    Grid, Alert, Breadcrumbs, Link as MuiLink, Tabs, Tab,
    Accordion, AccordionSummary, AccordionDetails, IconButton, Tooltip, Skeleton,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { useNotify } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import {
    Person as PersonIcon, Group as GroupIcon, Email as EmailIcon,
    CheckCircle as CheckCircleIcon, Cancel as CancelIcon,
    Info as InfoIcon, CalendarToday as CalendarTodayIcon,
    Payment as PaymentIcon, Refresh as RefreshIcon,
    CheckCircle as SuccessIcon, HourglassEmpty as PendingIcon,
    Error as FailedIcon, MoneyOff as RefundedIcon,
    ExpandMore as ExpandMoreIcon, ContentCopy as ContentCopyIcon,
    Star as StarIcon
} from '@mui/icons-material';

import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';
import { UsersGroupSettingsDialog } from './UsersGroupSettingsDialog';

const host = config.production.host;

const getJwt = () => localStorage.getItem("feathers-jwt");

const ROLE_COLORS = {
    admin: '#e85a2c',
    user: '#2196f3',
    guest: '#9e9e9e',
    moderator: '#ff9800',
    editor: '#4caf50',
    candidato: '#9c27b0',
    collaboratore: '#00bcd4'
};

const ROLE_LABELS = {
    admin: 'Amministratore',
    user: 'Utente',
    guest: 'Ospite',
    moderator: 'Moderatore',
    editor: 'Editor',
    candidato: 'Candidato',
    collaboratore: 'Collaboratore'
};

const getInitials = (nome, cognome) => {
    const n = nome ? nome.charAt(0).toUpperCase() : '';
    const c = cognome ? cognome.charAt(0).toUpperCase() : '';
    return `${n}${c}` || '?';
};

// Stripe transaction status colors and labels
const STRIPE_STATUS_COLORS = {
    pending: '#ff9800',
    succeeded: '#4caf50',
    failed: '#f44336',
    canceled: '#9e9e9e',
    refunded: '#2196f3'
};

const STRIPE_STATUS_ICONS = {
    pending: PendingIcon,
    succeeded: CheckCircleIcon,
    failed: FailedIcon,
    canceled: CancelIcon,
    refunded: RefundedIcon
};

const STRIPE_STATUS_LABELS = {
    pending: 'In Attesa',
    succeeded: 'Completato',
    failed: 'Fallito',
    canceled: 'Annullato',
    refunded: 'Rimborsato'
};

export const UsersDetailPage = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const notify = useNotify();

    // Stati
    const [user, setUser] = React.useState(null);
    const [gruppi, setGruppi] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [loadingGruppi, setLoadingGruppi] = React.useState(false);
    const [groupSettings, setGroupSettings] = React.useState({});
    const [activeTab, setActiveTab] = React.useState(0);
    const [transactions, setTransactions] = React.useState([]);
    const [loadingTransactions, setLoadingTransactions] = React.useState(false);
    const [subscription, setSubscription] = React.useState(null);
    const [subscriptionHistory, setSubscriptionHistory] = React.useState([]);
    const [loadingSubscription, setLoadingSubscription] = React.useState(false);
    const [postStats, setPostStats] = React.useState(null);
    const [loadingPostStats, setLoadingPostStats] = React.useState(false);
    const [debugDialog, setDebugDialog] = React.useState({ open: false, data: {} });

    // Group Settings Dialog State
    const [openSettingsDialog, setOpenSettingsDialog] = React.useState(false);
    const [selectedSettings, setSelectedSettings] = React.useState(null);

    const loadUser = async () => {
        loadUserDetails();
    };

    const handleOpenDebug = (data) => {
        setDebugDialog({ open: true, data });
    };

    const handleCloseDebug = () => {
        setDebugDialog({ open: false, data: {} });
    };

    const handleOpenSettings = (e, settings) => {
        e.stopPropagation(); // Prevent accordion expansion
        setSelectedSettings(settings);
        setOpenSettingsDialog(true);
    };

    const handleCloseSettings = () => {
        setOpenSettingsDialog(false);
        setSelectedSettings(null);
    };

    const handleSaveSettings = () => {
        loadUser(); // Reload user data to reflect changes
    };

    // Funzione per copiare negli appunti
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        notify('Copiato negli appunti!', { type: 'success' });
    };
    const loadUserDetails = async () => {
        try {
            setLoading(true);
            setError(null);
            const jwt = getJwt();

            const response = await axios.get(
                `${host}/users/${userId}`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` }
                }
            );

            setUser(response.data);

            // Carica i gruppi solo se non è admin
            if (response.data.roles !== 'admin') {
                loadUserGruppi(userId);
            }

            // Carica abbonamento
            loadUserSubscription(userId);

        } catch (error) {
            console.error("Errore nel recupero dettagli utente:", error);
            setError("Errore nel caricamento dei dettagli dell'utente");
            notify("Errore nel caricamento dei dettagli", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Carica i gruppi dell'utente e i relativi settings
    const loadUserGruppi = async (uid) => {
        try {
            setLoadingGruppi(true);
            const jwt = getJwt();

            // 1. Fetch group details (names, permissions) from procedure
            let groupsData = [];
            try {
                const procResponse = await axios.get(`${host}/proc-get-users-gruppi`, {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: { p_input_usersId: uid }
                });
                groupsData = procResponse.data?.data || [];
            } catch (procError) {
                console.error("Errore proc-get-users-gruppi:", procError);
                throw procError; // If this fails, we can't show anything
            }

            // 2. Fetch relationship details (flPrincipale) directly from table (Optional)
            let relData = [];
            try {
                const relResponse = await axios.get(`${host}/tbl-users-gruppo`, {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: { usersId: parseInt(uid, 10) } // Ensure ID is integer
                });
                relData = relResponse.data?.data || [];
            } catch (relError) {
                console.warn("Errore tbl-users-gruppo (ignoring):", relError);
                // Continue without flPrincipale info if this fails
            }

            // Merge flPrincipale into groupsData
            const groupsWithMain = groupsData.map(g => {
                const rel = relData.find(r => r.id === g.id);
                // If only one group exists, it's effectively main
                const isSingleGroup = groupsData.length === 1;
                return {
                    ...g,
                    flPrincipale: rel ? rel.flPrincipale : (isSingleGroup ? true : false)
                };
            });

            setGruppi(groupsWithMain);

            // Carica i settings per ogni gruppo trovato
            if (groupsWithMain.length > 0) {
                const groupIds = groupsWithMain.map(g => g.id);
                loadGroupsSettings(groupIds);
            }

        } catch (error) {
            console.error("Errore nel recupero gruppi:", error);
            setGruppi([]);
        } finally {
            setLoadingGruppi(false);
        }
    };

    // Helper per caricare i settings dei gruppi
    const loadGroupsSettings = async (groupIds) => {
        try {
            const jwt = getJwt();
            const response = await axios.get(`${host}/users-settings`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    tbl_users_gruppoId: { $in: groupIds },
                    $limit: 100
                }
            });

            const settingsData = response.data?.data || [];
            const settingsMap = {};
            settingsData.forEach(s => {
                settingsMap[s.tbl_users_gruppoId] = s;
            });

            setGroupSettings(settingsMap);

        } catch (error) {
            console.error("Errore nel recupero settings gruppi:", error);
        }
    };

    // Carica le transazioni Stripe dell'utente
    const loadUserTransactions = async (uid) => {
        try {
            setLoadingTransactions(true);
            const jwt = getJwt();

            const response = await axios.get(`${host}/tbl-transactions`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    usersId: uid,
                    $sort: { created_at: -1 },
                    $limit: 100
                }
            });

            setTransactions(response.data?.data || []);
        } catch (error) {
            console.error("Errore nel recupero transazioni:", error);
            setTransactions([]);
        } finally {
            setLoadingTransactions(false);
        }
    };

    // Carica abbonamento e storico
    const loadUserSubscription = async (uid) => {
        try {
            setLoadingSubscription(true);
            const jwt = getJwt();

            // 1. Get Active Subscription
            const subResponse = await axios.get(`${host}/tbl-users-piano`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    usersId: uid,
                    status: 'active',
                    $limit: 1
                }
            });

            const activeSub = subResponse.data?.data?.[0] || subResponse.data?.[0];

            if (activeSub) {
                // Fetch plan details
                try {
                    const planResponse = await axios.get(`${host}/tbl-piani/${activeSub.pianoId}`, {
                        headers: { 'Authorization': `Bearer ${jwt}` }
                    });
                    activeSub.piano = planResponse.data;
                } catch (e) {
                    console.error("Error fetching plan details", e);
                }
                setSubscription(activeSub);
            } else {
                setSubscription(null);
            }

            // 2. Get Subscription History
            // First get the subscription IDs for this user (active or not)
            const allSubsResponse = await axios.get(`${host}/tbl-users-piano`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    usersId: uid,
                    $select: ['id']
                }
            });

            const subIds = (allSubsResponse.data?.data || allSubsResponse.data || []).map(s => s.id);

            if (subIds.length > 0) {
                const historyResponse = await axios.get(`${host}/tbl-users-piano-transazione`, {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        usersPianoId: { $in: subIds },
                        $sort: { createdAt: -1 }
                    }
                });
                setSubscriptionHistory(historyResponse.data?.data || []);
            } else {
                setSubscriptionHistory([]);
            }

        } catch (error) {
            console.error("Errore nel recupero abbonamento:", error);
        } finally {
            setLoadingSubscription(false);
        }
    };

    // Carica statistiche post
    const loadUserPostStats = async (uid) => {
        try {
            setLoadingPostStats(true);
            const jwt = getJwt();

            const response = await axios.get(`${host}/dynamic-proc`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    proc_name: 'proc_get_check_limiti_post',
                    in_usersId: uid,
                    in_tipo_chiamata: 1 // check iniziale
                }
            });

            // La proc restituisce i dati in response.data.data.tmp_check_limiti_post
            const stats = response.data?.data?.tmp_check_limiti_post?.[0];
            setPostStats(stats);
        } catch (error) {
            console.error("Errore nel recupero statistiche post:", error);
            setPostStats(null);
        } finally {
            setLoadingPostStats(false);
        }
    };

    // Carica i dati al mount e quando cambia userId
    React.useEffect(() => {
        if (userId) {
            loadUserDetails();
        }
    }, [userId]);

    // Calcola lo stato dell'utente
    const getUserStatus = () => {
        if (!user) return { label: 'N/A', color: '#9e9e9e', bgcolor: '#f5f5f5' };

        const isActive = user.status === 1 || user.status === true;
        if (isActive) {
            return { label: 'Attivo', color: '#4caf50', bgcolor: '#e8f5e8' };
        } else {
            return { label: 'Disattivo', color: '#f44336', bgcolor: '#ffebee' };
        }
    };

    const status = getUserStatus();

    // Loading skeleton
    if (loading) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
                    <Box sx={{ mb: 3 }}>
                        <Skeleton variant="text" width={300} height={24} />
                    </Box>
                    <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Skeleton variant="circular" width={80} height={80} />
                        <Box sx={{ flex: 1 }}>
                            <Skeleton variant="text" width={200} height={32} />
                            <Skeleton variant="text" width={150} height={20} />
                        </Box>
                    </Box>
                    <Grid container spacing={3}>
                        {[1, 2, 3, 4].map(i => (
                            <Grid item xs={12} md={6} key={i}>
                                <Card sx={{ p: 3 }}>
                                    <Skeleton variant="text" width={120} height={24} />
                                    <Skeleton variant="text" width="100%" height={20} sx={{ mt: 1 }} />
                                    <Skeleton variant="text" width="80%" height={20} />
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
                    <Breadcrumbs sx={{ mb: 3 }}>
                        <MuiLink
                            component="button"
                            variant="body2"
                            onClick={() => navigate('/users')}
                            sx={{ textDecoration: 'none' }}
                        >
                            Utenti
                        </MuiLink>
                        <Typography variant="body2" color="text.primary">
                            Errore
                        </Typography>
                    </Breadcrumbs>

                    <Alert
                        severity="error"
                        sx={{ mb: 3 }}
                        action={
                            <Button color="inherit" size="small" onClick={loadUserDetails}>
                                Riprova
                            </Button>
                        }
                    >
                        {error}
                    </Alert>

                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/users')}
                    >
                        Torna alla lista
                    </Button>
                </motion.div>
            </Box>
        );
    }

    // Main content
    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                {/* Breadcrumb */}
                <Breadcrumbs sx={{ mb: 3 }}>
                    <MuiLink
                        component="button"
                        variant="body2"
                        onClick={() => navigate('/users')}
                        sx={{ textDecoration: 'none' }}
                    >
                        Utenti
                    </MuiLink>
                    <Typography variant="body2" color="text.primary">
                        {user?.email || `Utente ${userId}`}
                    </Typography>
                </Breadcrumbs>

                {/* Header con info principale */}
                <Card sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>

                        {/* Avatar */}
                        <Avatar
                            sx={{
                                width: 80,
                                height: 80,
                                bgcolor: ROLE_COLORS[user?.roles] || '#757575',
                                fontSize: '2rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {getInitials(user?.nome, user?.cognome)}
                        </Avatar>

                        {/* Info principale */}
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                {user?.nome && user?.cognome ? `${user.nome} ${user.cognome}` : user?.email || 'Nome non disponibile'}
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                                <EmailIcon sx={{ color: '#999', fontSize: 18 }} />
                                <Typography variant="body1" color="text.secondary">
                                    {user?.email}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                <Chip
                                    label={status.label}
                                    icon={status.label === 'Attivo' ? <CheckCircleIcon /> : <CancelIcon />}
                                    sx={{
                                        color: status.color,
                                        bgcolor: status.bgcolor,
                                        fontWeight: 500
                                    }}
                                />
                                <Chip
                                    icon={<PersonIcon sx={{ fontSize: 16 }} />}
                                    label={ROLE_LABELS[user?.roles] || user?.roles}
                                    sx={{
                                        bgcolor: ROLE_COLORS[user?.roles] || '#757575',
                                        color: '#fff',
                                        fontWeight: 500,
                                        '& .MuiChip-icon': { color: '#fff' }
                                    }}
                                />
                            </Box>

                            <Typography variant="body2" color="text.secondary">
                                ID Utente: {user?.id}
                            </Typography>
                        </Box>

                        {/* Azioni */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate('/users')}
                                size="small"
                            >
                                Indietro
                            </Button>

                            <Button
                                variant="contained"
                                startIcon={<EditIcon />}
                                onClick={() => navigate(`/users/${userId}/edit`)}
                                size="small"
                            >
                                Modifica
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={loadUserDetails}
                                size="small"
                            >
                                Aggiorna
                            </Button>
                        </Box>
                    </Box>
                </Card>

                {/* Tabs Navigation */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, newValue) => {
                            setActiveTab(newValue);
                            if (newValue === 2 && transactions.length === 0) {
                                loadUserTransactions(userId);
                            }
                            if (newValue === 3 && !subscription && !loadingSubscription) {
                                loadUserSubscription(userId);
                            }
                            if (newValue === 3 && !postStats && !loadingPostStats) {
                                loadUserPostStats(userId);
                            }
                        }}
                        aria-label="user detail tabs"
                    >
                        <Tab label="📋 Informazioni" />
                        <Tab label="👥 Gruppi" />
                        <Tab label="💳 Transazioni Stripe" />
                        <Tab label="⭐ Abbonamento" />
                    </Tabs>
                </Box>

                {/* Tab 0: Informazioni */}
                {activeTab === 0 && (
                    <Grid container spacing={3}>
                        {/* Informazioni Utente */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <InfoIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Informazioni Utente
                                    </Typography>
                                </Box>

                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 500, width: '40%' }}>Nome</TableCell>
                                            <TableCell>{user?.nome || 'N/A'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 500 }}>Cognome</TableCell>
                                            <TableCell>{user?.cognome || 'N/A'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 500 }}>Email</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <EmailIcon sx={{ fontSize: 16, color: '#999' }} />
                                                    {user?.email}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 500 }}>ID Utente</TableCell>
                                            <TableCell>{user?.id}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Card>
                        </Grid>

                        {/* Ruolo e Permessi */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <PersonIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Ruolo e Permessi
                                    </Typography>
                                </Box>

                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 500, width: '40%' }}>Ruolo</TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={<PersonIcon />}
                                                    label={ROLE_LABELS[user?.roles] || user?.roles}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: ROLE_COLORS[user?.roles] || '#757575',
                                                        color: '#fff',
                                                        fontWeight: 'bold',
                                                        '& .MuiChip-icon': { color: '#fff' }
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 500 }}>Stato</TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={status.label === 'Attivo' ? <CheckCircleIcon /> : <CancelIcon />}
                                                    label={status.label}
                                                    size="small"
                                                    color={status.label === 'Attivo' ? 'success' : 'error'}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Card>
                        </Grid>

                        {/* Timestamp */}
                        <Grid item xs={12}>
                            <Card sx={{ p: 3, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <CalendarTodayIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Timestamp
                                    </Typography>
                                </Box>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Table size="small">
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 500, width: '40%' }}>Creato il</TableCell>
                                                    <TableCell>
                                                        {user?.createdAt ?
                                                            new Date(user.createdAt).toLocaleString('it-IT') :
                                                            'N/A'
                                                        }
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Table size="small">
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 500, width: '40%' }}>Aggiornato il</TableCell>
                                                    <TableCell>
                                                        {user?.updatedAt ?
                                                            new Date(user.updatedAt).toLocaleString('it-IT') :
                                                            'N/A'
                                                        }
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Tab 1: Gruppi */}
                {activeTab === 1 && (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Card sx={{ p: 3, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <GroupIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Gruppi Assegnati
                                    </Typography>
                                </Box>

                                {loadingGruppi ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : gruppi.length > 0 ? (
                                    <Box>
                                        {gruppi.map((gruppo, index) => {
                                            const settings = groupSettings[gruppo.id];

                                            return (
                                                <Accordion key={index} sx={{ mb: 2, '&:before': { display: 'none' }, boxShadow: 1 }}>
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMoreIcon />}
                                                        sx={{
                                                            bgcolor: 'rgba(0, 0, 0, 0.02)',
                                                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2, flexWrap: 'wrap' }}>
                                                            {/* Gruppo Info */}
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
                                                                <GroupIcon color="primary" />
                                                                <Typography variant="subtitle1" fontWeight="bold">
                                                                    {gruppo.nome_gruppo}
                                                                </Typography>
                                                                {!!gruppo.flPrincipale && (
                                                                    <Tooltip title="Gruppo Principale">
                                                                        <StarIcon sx={{ color: '#FFD700' }} />
                                                                    </Tooltip>
                                                                )}
                                                            </Box>

                                                            {/* Permessi Badges */}
                                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                                <Tooltip title="Lettura">
                                                                    <Chip
                                                                        label="R"
                                                                        size="small"
                                                                        color={gruppo.read_permission ? "success" : "default"}
                                                                        variant={gruppo.read_permission ? "filled" : "outlined"}
                                                                        sx={{ width: 24, height: 24, '& .MuiChip-label': { px: 0 } }}
                                                                    />
                                                                </Tooltip>
                                                                <Tooltip title="Creazione">
                                                                    <Chip
                                                                        label="C"
                                                                        size="small"
                                                                        color={gruppo.create_permission ? "success" : "default"}
                                                                        variant={gruppo.create_permission ? "filled" : "outlined"}
                                                                        sx={{ width: 24, height: 24, '& .MuiChip-label': { px: 0 } }}
                                                                    />
                                                                </Tooltip>
                                                                <Tooltip title="Modifica">
                                                                    <Chip
                                                                        label="U"
                                                                        size="small"
                                                                        color={gruppo.update_permission ? "success" : "default"}
                                                                        variant={gruppo.update_permission ? "filled" : "outlined"}
                                                                        sx={{ width: 24, height: 24, '& .MuiChip-label': { px: 0 } }}
                                                                    />
                                                                </Tooltip>
                                                                <Tooltip title="Eliminazione">
                                                                    <Chip
                                                                        label="D"
                                                                        size="small"
                                                                        color={gruppo.remove_permission ? "success" : "default"}
                                                                        variant={gruppo.remove_permission ? "filled" : "outlined"}
                                                                        sx={{ width: 24, height: 24, '& .MuiChip-label': { px: 0 } }}
                                                                    />
                                                                </Tooltip>
                                                            </Box>

                                                            <Box sx={{ flex: 1 }} />

                                                            <Chip
                                                                label={gruppo.flAttivo ? 'Attivo' : 'Inattivo'}
                                                                size="small"
                                                                color={gruppo.flAttivo ? 'success' : 'default'}
                                                            />
                                                        </Box>
                                                    </AccordionSummary>
                                                    <AccordionDetails sx={{ bgcolor: '#fafafa', p: 3 }}>
                                                        {settings ? (
                                                            <Grid container spacing={3}>
                                                                {/* Intestazione Settings */}
                                                                <Grid item xs={12}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                                                                            ⚙️ Impostazioni Utente per questo Gruppo lato APP
                                                                        </Typography>
                                                                        {settings.isCompleted ? (
                                                                            <Chip label="Profilo Completato" size="small" color="success" icon={<CheckCircleIcon />} />
                                                                        ) : (
                                                                            <Chip label="Incompleto" size="small" color="warning" />
                                                                        )}
                                                                        <Box sx={{ flex: 1 }} />
                                                                        <Tooltip title="Modifica Impostazioni">
                                                                            <IconButton size="small" onClick={(e) => handleOpenSettings(e, settings)} sx={{ mr: 1 }}>
                                                                                <EditIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Tooltip title="Vedi JSON Raw">
                                                                            <IconButton size="small" onClick={() => handleOpenDebug(settings)}>
                                                                                <InfoIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Box>
                                                                </Grid>

                                                                {/* Colonna 1: Profilo e Contatti */}
                                                                <Grid item xs={12} md={6}>
                                                                    <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                                                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 2 }}>
                                                                            PROFILO & CONTATTI
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                                            <Box>
                                                                                <Typography variant="caption" color="text.secondary">Nome Società / Visualizzato</Typography>
                                                                                <Typography variant="body2" fontWeight="500">{settings.nomeSocieta || '-'}</Typography>
                                                                            </Box>
                                                                            <Box>
                                                                                <Typography variant="caption" color="text.secondary">Tipo Account</Typography>
                                                                                <Typography variant="body2">{settings.accountType}</Typography>
                                                                            </Box>
                                                                            <Box>
                                                                                <Typography variant="caption" color="text.secondary">Email</Typography>
                                                                                <Typography variant="body2">{settings.email || '-'}</Typography>
                                                                            </Box>
                                                                            <Box>
                                                                                <Typography variant="caption" color="text.secondary">Telefono</Typography>
                                                                                <Typography variant="body2">{settings.telefono || '-'}</Typography>
                                                                            </Box>
                                                                            <Box>
                                                                                <Typography variant="caption" color="text.secondary">Indirizzo</Typography>
                                                                                <Typography variant="body2">
                                                                                    {[settings.indirizzo, settings.citta, settings.provincia, settings.cap].filter(Boolean).join(', ') || '-'}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    </Card>
                                                                </Grid>

                                                                {/* Colonna 2: Preferenze e Social */}
                                                                <Grid item xs={12} md={6}>
                                                                    <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                                                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 2 }}>
                                                                            PREFERENZE & SOCIAL
                                                                        </Typography>

                                                                        <Grid container spacing={2}>
                                                                            <Grid item xs={12} sm={6}>
                                                                                <Box sx={{ mb: 2 }}>
                                                                                    <Typography variant="caption" color="text.secondary">Colori</Typography>
                                                                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                                                        <Tooltip title={`Primary: ${settings.primaryColor}`}><Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: settings.primaryColor, border: '1px solid #ddd' }} /></Tooltip>
                                                                                        <Tooltip title={`Secondary: ${settings.secondaryColor}`}><Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: settings.secondaryColor, border: '1px solid #ddd' }} /></Tooltip>
                                                                                        <Tooltip title={`Accent: ${settings.accentColor}`}><Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: settings.accentColor, border: '1px solid #ddd' }} /></Tooltip>
                                                                                    </Box>
                                                                                </Box>
                                                                            </Grid>
                                                                            <Grid item xs={12} sm={6}>
                                                                                <Box sx={{ mb: 2 }}>
                                                                                    <Typography variant="caption" color="text.secondary">Preferiti</Typography>
                                                                                    {settings.selectedTeamName && <Chip size="small" label={`Team: ${settings.selectedTeamName}`} sx={{ mt: 0.5, mr: 1 }} />}
                                                                                    {settings.selectedLegaName && <Chip size="small" label={`Lega: ${settings.selectedLegaName}`} sx={{ mt: 0.5 }} />}
                                                                                    {settings.has_no_favorite_team ? <Typography variant="caption" display="block">Nessun team preferito</Typography> : null}
                                                                                </Box>
                                                                            </Grid>
                                                                        </Grid>

                                                                        <Box sx={{ mt: 1 }}>
                                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Links</Typography>
                                                                            {settings.sitoWeb && <MuiLink href={settings.sitoWeb} target="_blank" sx={{ mr: 2, fontSize: '0.875rem' }}>Sito Web</MuiLink>}
                                                                            {settings.facebookUrl && <MuiLink href={settings.facebookUrl} target="_blank" sx={{ mr: 2, fontSize: '0.875rem' }}>Facebook</MuiLink>}
                                                                            {settings.instagramUrl && <MuiLink href={settings.instagramUrl} target="_blank" sx={{ mr: 2, fontSize: '0.875rem' }}>Instagram</MuiLink>}
                                                                        </Box>
                                                                    </Card>
                                                                </Grid>

                                                                {/* Colonna 3: Notifiche e Altro */}
                                                                <Grid item xs={12}>
                                                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                                        <Chip label="Risultati" color={settings.notifiche_risultati ? "success" : "default"} variant={settings.notifiche_risultati ? "filled" : "outlined"} size="small" icon={settings.notifiche_risultati ? <CheckCircleIcon /> : <CancelIcon />} />
                                                                        <Chip label="News" color={settings.notifiche_news ? "success" : "default"} variant={settings.notifiche_news ? "filled" : "outlined"} size="small" icon={settings.notifiche_news ? <CheckCircleIcon /> : <CancelIcon />} />
                                                                        <Chip label="Live" color={settings.notifiche_live ? "success" : "default"} variant={settings.notifiche_live ? "filled" : "outlined"} size="small" icon={settings.notifiche_live ? <CheckCircleIcon /> : <CancelIcon />} />
                                                                        <Chip label="Profilo Pubblico" color={settings.profilo_pubblico ? "primary" : "default"} variant={settings.profilo_pubblico ? "filled" : "outlined"} size="small" />
                                                                        <Chip label="Statistiche Condivise" color={settings.condiviso_statistiche ? "primary" : "default"} variant={settings.condiviso_statistiche ? "filled" : "outlined"} size="small" />
                                                                    </Box>
                                                                </Grid>
                                                            </Grid>
                                                        ) : (
                                                            <Alert severity="info" variant="outlined">
                                                                Nessuna impostazione trovata per questo gruppo.
                                                            </Alert>
                                                        )}
                                                    </AccordionDetails>
                                                </Accordion>
                                            );
                                        })}
                                    </Box>
                                ) : (
                                    <Alert severity="warning">
                                        ⚠️ Nessun gruppo assegnato a questo utente
                                    </Alert>
                                )}
                            </Card>
                        </Grid>

                        {/* Admin message */}
                        {user?.roles === 'admin' && (
                            <Grid item xs={12}>
                                <Alert severity="info">
                                    Gli amministratori hanno accesso completo al sistema e non necessitano di gruppi specifici.
                                </Alert>
                            </Grid>
                        )}
                    </Grid>
                )}

                {/* Tab 2: Transazioni Stripe */}
                {activeTab === 2 && (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Card sx={{ p: 3, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PaymentIcon color="primary" />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Transazioni Stripe
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={() => loadUserTransactions(userId)}
                                        size="small"
                                    >
                                        Aggiorna
                                    </Button>
                                </Box>

                                {loadingTransactions ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : transactions.length > 0 ? (
                                    <Box>
                                        {transactions.map((transaction, index) => {
                                            const status = transaction.status || 'pending';
                                            const StatusIcon = STRIPE_STATUS_ICONS[status];
                                            const planInfo = transaction.metadata?.planInfo || {};

                                            return (
                                                <Accordion key={transaction.id} sx={{ mb: 2 }}>
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMoreIcon />}
                                                        sx={{
                                                            bgcolor: 'rgba(0, 0, 0, 0.02)',
                                                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                                                            <Chip
                                                                icon={<StatusIcon />}
                                                                label={STRIPE_STATUS_LABELS[status]}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: STRIPE_STATUS_COLORS[status],
                                                                    color: '#fff',
                                                                    fontWeight: 'bold',
                                                                    '& .MuiChip-icon': { color: '#fff' }
                                                                }}
                                                            />
                                                            <Typography variant="body1" fontWeight="bold">
                                                                {(transaction.amount / 100).toFixed(2)} {(transaction.currency || 'EUR').toUpperCase()}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                                                                {transaction.description || 'N/A'}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {transaction.created_at ?
                                                                    new Date(transaction.created_at).toLocaleDateString('it-IT') :
                                                                    'N/A'
                                                                }
                                                            </Typography>
                                                        </Box>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <Grid container spacing={2}>
                                                            {/* Dettagli Transazione */}
                                                            <Grid item xs={12}>
                                                                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                                                                    📋 Dettagli Transazione
                                                                </Typography>
                                                            </Grid>

                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="caption" color="text.secondary">ID Transazione</Typography>
                                                                <Typography variant="body2" fontWeight="500">#{transaction.id}</Typography>
                                                            </Grid>

                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="caption" color="text.secondary">Importo</Typography>
                                                                <Typography variant="body2" fontWeight="bold" color="primary">
                                                                    {(transaction.amount / 100).toFixed(2)} {(transaction.currency || 'EUR').toUpperCase()}
                                                                </Typography>
                                                            </Grid>

                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="caption" color="text.secondary">Data Creazione</Typography>
                                                                <Typography variant="body2">
                                                                    {transaction.created_at ?
                                                                        new Date(transaction.created_at).toLocaleString('it-IT') :
                                                                        'N/A'
                                                                    }
                                                                </Typography>
                                                            </Grid>

                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="caption" color="text.secondary">Ultimo Aggiornamento</Typography>
                                                                <Typography variant="body2">
                                                                    {transaction.updated_at ?
                                                                        new Date(transaction.updated_at).toLocaleString('it-IT') :
                                                                        'N/A'
                                                                    }
                                                                </Typography>
                                                            </Grid>

                                                            <Grid item xs={12}>
                                                                <Typography variant="caption" color="text.secondary">Descrizione</Typography>
                                                                <Typography variant="body2">{transaction.description || 'Nessuna descrizione'}</Typography>
                                                            </Grid>

                                                            <Grid item xs={12}>
                                                                <Typography variant="caption" color="text.secondary">Payment Intent ID</Typography>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontFamily: 'monospace',
                                                                            fontSize: '0.875rem',
                                                                            bgcolor: 'rgba(0, 0, 0, 0.05)',
                                                                            p: 0.5,
                                                                            borderRadius: 1,
                                                                            flex: 1
                                                                        }}
                                                                    >
                                                                        {transaction.stripe_payment_intent_id || 'N/A'}
                                                                    </Typography>
                                                                    {transaction.stripe_payment_intent_id && (
                                                                        <Tooltip title="Copia Payment Intent ID">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => copyToClipboard(transaction.stripe_payment_intent_id)}
                                                                                sx={{ color: 'primary.main' }}
                                                                            >
                                                                                <ContentCopyIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </Box>
                                                            </Grid>

                                                            {/* Dettagli Piano */}
                                                            {planInfo && Object.keys(planInfo).length > 0 && (
                                                                <>
                                                                    <Grid item xs={12} sx={{ mt: 2 }}>
                                                                        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                                                                            💎 Dettagli Piano
                                                                        </Typography>
                                                                    </Grid>

                                                                    {planInfo.nome && (
                                                                        <Grid item xs={12} md={6}>
                                                                            <Typography variant="caption" color="text.secondary">Nome Piano</Typography>
                                                                            <Typography variant="body2" fontWeight="600">{planInfo.nome}</Typography>
                                                                        </Grid>
                                                                    )}

                                                                    {planInfo.prezzo && (
                                                                        <Grid item xs={12} md={6}>
                                                                            <Typography variant="caption" color="text.secondary">Prezzo Piano</Typography>
                                                                            <Typography variant="body2">{planInfo.prezzo} €</Typography>
                                                                        </Grid>
                                                                    )}

                                                                    {planInfo.maxPostMonth && (
                                                                        <Grid item xs={12} md={6}>
                                                                            <Typography variant="caption" color="text.secondary">Max Post/Mese</Typography>
                                                                            <Typography variant="body2">{planInfo.maxPostMonth}</Typography>
                                                                        </Grid>
                                                                    )}

                                                                    {planInfo.watermark !== undefined && (
                                                                        <Grid item xs={12} md={6}>
                                                                            <Typography variant="caption" color="text.secondary">Watermark</Typography>
                                                                            <Chip
                                                                                label={planInfo.watermark ? 'Sì' : 'No'}
                                                                                size="small"
                                                                                color={planInfo.watermark ? 'default' : 'success'}
                                                                            />
                                                                        </Grid>
                                                                    )}
                                                                </>
                                                            )}
                                                        </Grid>
                                                    </AccordionDetails>
                                                </Accordion>
                                            );
                                        })}
                                    </Box>
                                ) : (
                                    <Alert severity="info">
                                        📭 Nessuna transazione trovata per questo utente
                                    </Alert>
                                )}
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Tab 3: Abbonamento */}
                {activeTab === 3 && (
                    <Grid container spacing={3}>
                        {/* Active Subscription */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <CheckCircleIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Abbonamento Attivo
                                    </Typography>
                                </Box>

                                {loadingSubscription ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : subscription ? (
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Piano</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={subscription.piano?.nome || 'Sconosciuto'}
                                                        color="primary"
                                                        size="small"
                                                        sx={{ fontWeight: 'bold' }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Stato</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={subscription.status}
                                                        color={subscription.status === 'active' ? 'success' : 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Durata</TableCell>
                                                <TableCell sx={{ textTransform: 'capitalize' }}>{subscription.durataType}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Inizio</TableCell>
                                                <TableCell>{new Date(subscription.dataInizio).toLocaleDateString('it-IT')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Scadenza</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                                    {new Date(subscription.dataScadenza).toLocaleDateString('it-IT')}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Rinnovo Auto</TableCell>
                                                <TableCell>{subscription.autoRinnovo ? 'Sì' : 'No'}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <Alert severity="info">Nessun abbonamento attivo trovato.</Alert>
                                )}
                            </Card>
                        </Grid>

                        {/* Subscription History */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <CalendarTodayIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Storico Eventi
                                    </Typography>
                                </Box>

                                {loadingSubscription ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : subscriptionHistory.length > 0 ? (
                                    <TableContainer sx={{ maxHeight: 300 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Data</TableCell>
                                                    <TableCell>Tipo</TableCell>
                                                    <TableCell>Periodo</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {subscriptionHistory.map((event) => (
                                                    <TableRow key={event.id}>
                                                        <TableCell>{new Date(event.createdAt || event.periodoInizio).toLocaleDateString('it-IT')}</TableCell>
                                                        <TableCell sx={{ textTransform: 'capitalize' }}>{event.tipo}</TableCell>
                                                        <TableCell>
                                                            {new Date(event.periodoInizio).toLocaleDateString('it-IT')} - {new Date(event.periodoFine).toLocaleDateString('it-IT')}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Alert severity="info">Nessun evento storico trovato.</Alert>
                                )}
                            </Card>
                        </Grid>

                        {/* Statistiche Post */}
                        <Grid item xs={12}>
                            <Card sx={{ p: 3, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <InfoIcon color="primary" />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Statistiche Post
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={() => loadUserPostStats(userId)}
                                        size="small"
                                    >
                                        Aggiorna
                                    </Button>
                                </Box>

                                {loadingPostStats ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : postStats ? (
                                    <Box>
                                        {/* Riepilogo Esito */}
                                        <Alert
                                            severity={postStats.checkEsito === 'OK' ? "success" : "warning"}
                                            sx={{ mb: 3 }}
                                        >
                                            <strong>{postStats.checkEsitoDescrizione}</strong>
                                            {postStats.pianoDescrizione && (
                                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                    Piano di riferimento: <strong>{postStats.pianoDescrizione}</strong>
                                                    {postStats.gruppoDescrizione ? ` (${postStats.gruppoDescrizione})` : ''}
                                                </Typography>
                                            )}
                                        </Alert>

                                        <Grid container spacing={3}>
                                            {/* Daily Stats */}
                                            <Grid item xs={12} md={4}>
                                                <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderColor: postStats.nPostGgCheck === 'OK' ? 'success.light' : 'warning.light' }}>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>GIORNALIERO</Typography>
                                                    <Typography variant="h4" sx={{ fontWeight: 700, color: postStats.nPostGgCheck === 'OK' ? 'success.main' : 'warning.main' }}>
                                                        {postStats.nPostGgDone} / {postStats.maxPostGg}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Rimasti: <strong>{postStats.nPostGgToDo}</strong>
                                                    </Typography>
                                                    <Box sx={{ mt: 1 }}>
                                                        <Chip
                                                            label={postStats.nPostGgCheck}
                                                            size="small"
                                                            color={postStats.nPostGgCheck === 'OK' ? 'success' : 'warning'}
                                                        />
                                                    </Box>
                                                </Card>
                                            </Grid>

                                            {/* Weekly Stats */}
                                            <Grid item xs={12} md={4}>
                                                <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderColor: postStats.nPostWeekCheck === 'OK' ? 'success.light' : 'warning.light' }}>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>SETTIMANALE</Typography>
                                                    <Typography variant="h4" sx={{ fontWeight: 700, color: postStats.nPostWeekCheck === 'OK' ? 'success.main' : 'warning.main' }}>
                                                        {postStats.nPostWeekDone} / {postStats.maxPostWeek}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Rimasti: <strong>{postStats.nPostWeekToDo}</strong>
                                                    </Typography>
                                                    <Box sx={{ mt: 1 }}>
                                                        <Chip
                                                            label={postStats.nPostWeekCheck}
                                                            size="small"
                                                            color={postStats.nPostWeekCheck === 'OK' ? 'success' : 'warning'}
                                                        />
                                                    </Box>
                                                </Card>
                                            </Grid>

                                            {/* Monthly Stats */}
                                            <Grid item xs={12} md={4}>
                                                <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderColor: postStats.nPostMonthCheck === 'OK' ? 'success.light' : 'warning.light' }}>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>MENSILE</Typography>
                                                    <Typography variant="h4" sx={{ fontWeight: 700, color: postStats.nPostMonthCheck === 'OK' ? 'success.main' : 'warning.main' }}>
                                                        {postStats.nPostMonthDone} / {postStats.maxPostMonth}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Rimasti: <strong>{postStats.nPostMonthToDo}</strong>
                                                    </Typography>
                                                    <Box sx={{ mt: 1 }}>
                                                        <Chip
                                                            label={postStats.nPostMonthCheck}
                                                            size="small"
                                                            color={postStats.nPostMonthCheck === 'OK' ? 'success' : 'warning'}
                                                        />
                                                    </Box>
                                                </Card>
                                            </Grid>
                                        </Grid>

                                        {/* Debug info if needed */}
                                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                            <Tooltip title="Vedi Dati Raw">
                                                <IconButton size="small" onClick={() => handleOpenDebug(postStats)}>
                                                    <InfoIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Alert severity="info">Nessuna statistica post disponibile.</Alert>
                                )}
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Footer con ultimo aggiornamento */}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Ultimo aggiornamento: {new Date().toLocaleString('it-IT')}
                    </Typography>
                </Box>

            </motion.div>

            {/* Debug Dialog */}
            <Dialog open={debugDialog.open} onClose={handleCloseDebug} maxWidth="md" fullWidth>
                <DialogTitle>
                    Debug: Raw JSON Settings
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDebug}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CancelIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box
                        component="pre"
                        sx={{
                            bgcolor: '#f5f5f5',
                            p: 2,
                            borderRadius: 1,
                            overflow: 'auto',
                            maxHeight: 500,
                            fontSize: '0.8rem'
                        }}
                    >
                        {JSON.stringify(debugDialog.data, null, 2)}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => copyToClipboard(JSON.stringify(debugDialog.data, null, 2))} startIcon={<ContentCopyIcon />}>
                        Copia JSON
                    </Button>
                    <Button onClick={handleCloseDebug}>Chiudi</Button>
                </DialogActions>
            </Dialog>

            {/* Edit Settings Dialog */}
            <UsersGroupSettingsDialog
                open={openSettingsDialog}
                onClose={handleCloseSettings}
                settings={selectedSettings}
                onSave={handleSaveSettings}
            />
        </Box>
    );
};
